import i18n from '@/plugins/vue-i18n';
import { getOrders } from '@/api/organisations.service';
import type { AxiosError } from 'axios';
import { Pagination, type ErrorResponse, type RequestConfig } from '@/types/api/response.types';
import { defineStore } from 'pinia';
import { isBulkPaymentRequest } from '@/common/utils/bulk-payment-request.utils';
import { useToastStore } from '@/stores/utils/toast.store';
import { sayHandledConflict } from '@/common/api/handled-conflict';
import { useLoaderStore } from '@/stores/utils/loader.store';
import {
  addItemContraCharge,
  addRetention,
  addValueContraCharge,
  createOrders,
  deleteItemContraCharge,
  deleteOrderDocument,
  deleteRetention,
  deleteValueContraCharge,
  editItemContraCharge,
  editRetention,
  editValueContraCharge,
  exportOrder,
  getOrderById,
  getOrderComplianceInfo,
  getOrderDocs,
  getOrderPaymentRequestsById,
  getOrderStatuses,
  getOrderTotals,
  logOrderDelivery,
  shareOrder,
  toggleOrderPlaced,
  updateOrderStatus,
  updateOrderVariationStatus,
  uploadOrderDocuments,
} from '@/api/orders.service';
import {
  Addition,
  ComplianceInfo,
  CreateOrdersBody,
  DocumentUploadResponse,
  GetOrdersParams,
  ItemContraChargeRequest,
  LocalDepositTracking,
  OrderClaim,
  OrderDelivery,
  OrderListItem,
  OrderStatusUpdateBody,
  ItemVariationReviewBody,
  OrderTotalParams,
  OrderTotalType,
  Retention,
  ShareOrder,
  ValueContraChargeData,
} from '@/types/api/orders.types';
import { useCartsStore, VAT_MULTIPLIER } from '@/stores/carts.store';
import { documentUploadToast, exportFile } from '@/common/utils/general.utils';
import { BOQItem } from '@/types/api/sites.types';
import { useVendorStore } from '@/stores/vendors.store';
import { Vendor } from '@/types/api/vendors.types';
import { structureItemsWithDeliveries } from '@/common/utils/orders.utils';
import { ReturnOrderItem } from '@/types/api/returns.types';
import { useBoqStore } from '@/stores/boqs.store';
import { settleLoading } from '@/stores/utils/global-loader';
import { toRefs } from 'vue';
import { useTotalsStore } from '@/stores/utils/totals.store';
import { PaymentRequest } from '@/types/api/organisations.types';
import { analyticsService } from '@/plugins/post-hog';
import { selectedOrgId } from '@/common/utils/role.utils';
import { isBulkChildOrder } from '@/common/utils/bulk-orders.utils';

const { t } = i18n.global;

/**
 * A bulk site order's upload or delete refusal is shown where it happened as well as toasted
 * (DL-144), so its caller handles the 400 rather than leaving it to the default path (PE-1053).
 */
const BULK_SITE_ORDER_DOCUMENT_CONFIG: Partial<RequestConfig> = { handlesErrors: [400] };

/**
 * Loader name for a totals fetch, keyed by type so an order fetch and a claim fetch
 * don't clear each other's loader.
 *
 * Exported because the totals are fetched and consumed in different components —
 * `OrdersView` / `DefaultLayout` trigger the fetch, while the summary cards that need
 * to know about it live in a sibling — so a consumer has to be able to name the same
 * loader without hardcoding the string.
 *
 * Caveat worth knowing: `loaderStore.activeLoaders` is a uniq'd array rather than a
 * counter, so two concurrent fetches of the SAME type share one entry and the first to
 * finish clears it. Sequential and cross-type calls are unaffected.
 */
export const orderTotalsLoaderName = (type: OrderTotalType): string => `order-totals-${type}`;

// The order and claim lists each keep their latest read: a filter, page or link can start a new
// one before the last answers, and an earlier read landing after it would put the old list on screen.
const latestListRead = { Order: { id: 0, query: '' }, Claim: { id: 0, query: '' } };

export const useOrdersStore = defineStore('orders', {
  state: () => ({
    order: {} as OrderClaim,
    orderList: [] as OrderListItem[],
    claimList: [] as OrderListItem[],
    orderStatuses: [] as string[],
    claimStatuses: [] as string[],
    orderPayments: [] as PaymentRequest[],
    orderPagination: {} as Pagination,
    claimPagination: {} as Pagination,
    exportedOrder: null as Blob | null,
    orderDocs: [] as DocumentUploadResponse[],
    orderCartItems: [] as BOQItem[],
    cartItemsGroupedByVendor: [] as LocalDepositTracking[],
    additions: [] as Addition[],
    orderClaim: {} as OrderClaim,
    retentions: [] as Retention[],
    itemContraCharges: [] as BOQItem[],
    valueContraCharges: [] as ValueContraChargeData[],
    newOrderActiveTab: '#order',
    itemsWithDeliveries: [] as ReturnOrderItem[],
    orderQueryString: '',
    claimQueryString: '',
    viewingReturnDetails: false,
    // Set while the order drawer has drilled into a linked Payment Request (PE-390), so the
    // shared OrderDetailsHeader can swap to the PR header — mirrors `viewingReturnDetails`.
    viewingLinkedPaymentRequest: false,
    completeClaim: false,
    hasPendingClaimDocuments: false,
    exitClaimRequested: false,
    complianceInfo: {} as ComplianceInfo,
    /** The order ID for which complianceInfo was last resolved (success or graceful failure). */
    complianceInfoOrderId: null as string | null,
    /**
    A bulk site order's refused upload or delete, in the server's words, for Key documentation.
    */
    documentRefusal: null as { orderId: string; message: string } | null,
  }),

  actions: {
    async createOrders(data: CreateOrdersBody, siteId?: string): Promise<OrderClaim[]> {
      const boqStore = useBoqStore();
      const cartStore = useCartsStore();
      const toastStore = useToastStore();
      const response = await createOrders(data);

      if (response.status === 200) {
        toastStore.showToast('success', t('orders.createOrder.toast.title'));

        const createdOrders = response.data.data;

        for (const order of createdOrders) {
          analyticsService.capture('boq_order_created', {
            org_id: selectedOrgId.value!,
            order_id: order.id,
            boq_id: order.boqId,
          });
        }

        if (siteId && boqStore.hasBoqForSite(siteId)) {
          await boqStore.getBOQ(siteId, true);
        }

        cartStore.clearCartStore();

        return createdOrders;
      }

      return [];
    },

    /**
     * Whether a site has any orders against it. A yes/no question, not a list load: it returns the
     * answer and deliberately leaves `orderList` (and its cache key) untouched, so a caller can ask
     * about one site without clobbering the list a table elsewhere is showing.
     *
     * Use this rather than `getOrders({ siteId })` + reading `orderList` — that only assigns
     * `orderList` when `params.type === 'Order'`, so a call without a type leaves the list at
     * whatever a previous view put there and the answer is whatever that list happened to hold.
     */
    async hasOrdersForSite(orgId: string, siteId: string): Promise<boolean> {
      const response = await getOrders(orgId, {
        pageNo: 1,
        pageSize: 1,
        siteId,
        type: 'Order',
      });

      return response.status === 200 && response.data.data.length > 0;
    },

    async getOrders(orgId: string, params: GetOrdersParams, forceRefresh = false) {
      const query = JSON.stringify({ orgId, ...params });
      const latest =
        params.type === 'Claim' || params.type === 'Order'
          ? latestListRead[params.type]
          : undefined;
      if (
        !forceRefresh &&
        ((params.type === 'Claim' &&
          this.claimList.length > 0 &&
          this.claimQueryString === query) ||
          (params.type === 'Order' && this.orderList.length > 0 && this.orderQueryString === query))
      ) {
        // The cached list answers this query, so a read still in flight for another one is stale;
        // one for this same query (a forced refresh after a write) is newer than the cache and stays.
        if (latest && latest.query !== query) {
          latest.id++;
          latest.query = query;
        }
        return;
      }

      const read = latest && ++latest.id;
      if (latest) latest.query = query;
      const response = await getOrders(orgId, params);
      if (latest && read !== latest.id) return;

      if (response.status === 200) {
        if (params.type === 'Claim') {
          this.claimPagination = response.data.meta.pagination;
          this.claimList = response.data.data;
          this.claimQueryString = query;
        } else {
          this.orderPagination = response.data.meta.pagination;
          if (params.type === 'Order') {
            this.orderList = response.data.data;
            this.orderQueryString = query;
          }
        }
      }
    },

    /** Drops every order and claim list read in flight, so none can write back a list from before now. */
    invalidateListReads() {
      latestListRead.Order.id++;
      latestListRead.Claim.id++;
    },

    /**
     * Makes the Orders tab re-read its list on its next mount. `getOrders` serves the cached list
     * while the query is unchanged, so a write made outside the tab (a bulk placement creating
     * child orders, say) would otherwise stay invisible until the filter moved.
     */
    invalidateOrderList() {
      this.orderQueryString = '';
    },

    addAddition(vendorId: string, vendorName: string, type: 'deposit' | 'retention') {
      const newAddition: Addition = {
        id: `${type}-${vendorId}-${Date.now()}`,
        type,
        vendorId,
        vendorName,
        percent: 0,
        paymentType: 'deposit',
        dueDate: '',
        assigneeId: '',
      };

      this.additions.push(newAddition);

      // Also add to the vendor's additions array
      const vendor = this.cartItemsGroupedByVendor.find((v) => v.vendorId === vendorId);
      if (vendor) {
        if (!vendor.additions) {
          vendor.additions = [];
        }
        vendor.additions.push(newAddition);
      }
    },

    removeAddition(additionId: string) {
      // Remove from main additions array
      this.additions = this.additions.filter((a) => a.id !== additionId);

      // Remove from vendor's additions array
      for (const vendor of this.cartItemsGroupedByVendor) {
        if (vendor.additions) {
          vendor.additions = vendor.additions.filter((a) => a.id !== additionId);
        }
      }
    },

    hasDepositAddition(vendorId: string): boolean {
      return this.additions.some((a) => a.vendorId === vendorId && a.type === 'deposit');
    },

    getVendorAdditions(vendorId: string): Addition[] {
      return this.additions.filter((a) => a.vendorId === vendorId);
    },

    async getOrderStatuses(params?: { orderType: string }) {
      if (!params?.orderType || !['Order', 'Claim'].includes(params.orderType)) {
        console.warn('Invalid or missing orderType. Expected "Order" or "Claim".');
        return;
      }

      const isOrder = params.orderType === 'Order';
      const targetStatusArray = isOrder ? this.orderStatuses : this.claimStatuses;

      if (targetStatusArray.length === 0) {
        try {
          const response = await getOrderStatuses(params);

          if (response.status === 200) {
            if (isOrder) {
              this.orderStatuses = response.data.data;
            } else {
              this.claimStatuses = response.data.data;
            }
          } else {
            console.error(
              `Failed to fetch ${params.orderType} statuses. Status: ${response.status}`,
            );
          }
        } catch (error) {
          console.error(`An error occurred while fetching ${params.orderType} statuses:`, error);
        }
      }
    },

    async getOrderDetailsById(id: string) {
      const response = await getOrderById(id);

      if (response.status === 200) {
        this.order = response.data.data;
        this.orderClaim = response.data.data;

        this.itemsWithDeliveries = structureItemsWithDeliveries(
          this.order.items,
          this.order.returns,
        );
      }
    },

    clearDocumentRefusal() {
      this.documentRefusal = null;
    },

    /**
     * Holds a bulk site order's 400 so Key documentation can show it in place, and re-reads the
     * order so its rows show what the bulk order now holds. `true` when it was one.
     *
     * Context: the refusal is a backstop for the documents a drawer read before the bulk order's
     * copy reached the site order, since that copy arrives asynchronously (FR-38, PE-635).
     */
    async holdBulkSiteOrderRefusal(id: string, error: unknown): Promise<boolean> {
      const response = (error as AxiosError<ErrorResponse>)?.response;
      const firstError = response?.data?.errors?.[0];
      // PE-635's refusals are rule violations; a file the server rejects as invalid is not one.
      const message =
        firstError?.errorType === 'RuleViolation' ? firstError.displayMessage?.trim() : undefined;
      if (response?.status !== 400 || !message) return false;

      this.documentRefusal = { orderId: id, message };
      // Another order opened while the request ran would be replaced by this one's re-read.
      if (this.order.id === id) await this.getOrderDetailsById(id).catch(() => undefined);
      return true;
    },

    async uploadDocuments(id: string, data: FormData) {
      const isBulkSiteOrder = this.order.id === id && isBulkChildOrder(this.order);
      this.clearDocumentRefusal();

      try {
        const response = await uploadOrderDocuments(
          id,
          data,
          isBulkSiteOrder ? BULK_SITE_ORDER_DOCUMENT_CONFIG : undefined,
        );

        if (response.status < 200 || response.status >= 300) {
          throw new Error(`Document upload failed with status ${response.status}`);
        }

        documentUploadToast({
          responseCode: response.status,
          title: t('orders.orderDetails.uploadSection.toast.upload.success.title'),
          description: t('orders.orderDetails.uploadSection.toast.upload.success.content'),
          onSuccess: async () => {
            await this.getOrderDetailsById(id);
          },
          onError: () => {
            console.error('Document upload failed');
          },
        });
      } catch (error) {
        if (isBulkSiteOrder && (await this.holdBulkSiteOrderRefusal(id, error))) return;
        console.error('Error uploading documents:', error);
        throw error;
      }
    },

    async deleteDocument(id: string, docId: string) {
      const toastStore = useToastStore();
      const isBulkSiteOrder = this.order.id === id && isBulkChildOrder(this.order);
      this.clearDocumentRefusal();

      try {
        const response = await deleteOrderDocument(
          id,
          docId,
          isBulkSiteOrder ? BULK_SITE_ORDER_DOCUMENT_CONFIG : undefined,
        );

        if (response.status === 204) {
          toastStore.showToast(
            'success',
            t('orders.orderDetails.uploadSection.toast.delete.success.title'),
            t('orders.orderDetails.uploadSection.toast.delete.success.content'),
          );
        } else {
          toastStore.showToast(
            'error',
            t('orders.orderDetails.uploadSection.toast.delete.error.title'),
            t('orders.orderDetails.uploadSection.toast.delete.error.content'),
          );
        }
      } catch (error) {
        if (isBulkSiteOrder && (await this.holdBulkSiteOrderRefusal(id, error))) return;
        throw error;
      }
    },

    async updateVariationStatus(
      cartId: string,
      itemId: string,
      data: ItemVariationReviewBody,
      siteId?: string,
    ) {
      const boqStore = useBoqStore();
      const toastStore = useToastStore();
      const response = await updateOrderVariationStatus(cartId, itemId, data);

      if (response.status === 200) {
        toastStore.showToast(
          'success',
          t('sites.boq.updateVariationStatusModal.updateVariationStatusSuccess.title', {
            update: data.status.toLowerCase(),
          }),
          t('sites.boq.updateVariationStatusModal.updateVariationStatusSuccess.content', {
            update: data.status.toLowerCase(),
          }),
        );
        this.order = response.data.data;

        // The decision has saved by now, so a failed refresh of the cached bill must not reject it:
        // a caller would report a saved decision as refused. The interceptor has toasted the read.
        if (siteId && boqStore.hasBoqForSite(siteId)) {
          await settleLoading(boqStore.getBOQ(siteId, true));
        }
      }
    },

    async updateOrderStatus(id: string, data: OrderStatusUpdateBody, siteId?: string) {
      const boqStore = useBoqStore();
      let response: Awaited<ReturnType<typeof updateOrderStatus>>;
      try {
        response = await updateOrderStatus(id, data);
      } catch (error) {
        sayHandledConflict(error);
        throw error;
      }

      if (response.status === 200) {
        this.order = response.data.data;
        let matchFound = false;

        this.orderList = this.orderList.map((order) => {
          if (order.id === id) {
            matchFound = true;
            return { ...order, status: data.status };
          }

          return order;
        });

        this.claimList = this.claimList.map((claim) => {
          if (claim.id === id) {
            matchFound = true;
            return { ...claim, status: data.status };
          }
          return claim;
        });

        if (!matchFound) {
          console.warn(`Order or claim with ID ${id} not found in orderList or claimList.`);
        }

        if (siteId && boqStore.hasBoqForSite(siteId)) {
          await boqStore.getBOQ(siteId, true);
        }

        analyticsService.capture(`order_${data.status === 'Approved' ? 'approved' : 'declined'}`, {
          org_id: selectedOrgId.value!,
          order_id: id,
        });
      }
    },

    async getOrderPaymentRequests(orgId: string, orderId: string) {
      const response = await getOrderPaymentRequestsById(orgId, orderId);

      if (response.status === 200) {
        // Context: the read also serves a bulk site order's bulk requests (PE-957), which settle
        // the whole bulk order, not this order, so an order lists none of them (DL-236 D-04).
        this.orderPayments = response.data.data.filter(
          (paymentRequest: PaymentRequest) => !isBulkPaymentRequest(paymentRequest),
        );
      }
    },

    async processOrderDelivery(
      id: string,
      data: OrderDelivery[],
      version: number,
      siteId?: string,
    ) {
      const boqStore = useBoqStore();

      await logOrderDelivery(id, data, version);
      await this.getOrderDetailsById(id);

      analyticsService.capture('order_delivery_logged', {
        org_id: selectedOrgId.value!,
        order_id: id,
      });

      if (siteId && boqStore.hasBoqForSite(siteId)) {
        await boqStore.getBOQ(siteId, true);
      }
    },

    async getOrderTotals(params: OrderTotalParams, type: OrderTotalType) {
      const loaderStore = useLoaderStore();
      const loaderName = orderTotalsLoaderName(type);
      const {
        ordersByOrgCount,
        claimsByOrgCount,
        ordersBySiteCount,
        ordersByVendorCount,
        claimsBySiteCount,
        claimsByVendorCount,
        orderTotalsByOrg,
        orderTotalsByVendor,
        claimTotalsByOrg,
        claimTotalsByVendor,
      } = toRefs(useTotalsStore());

      // `try/finally` around the loader rather than `createActionHelpers.startLoading`:
      // that helper rethrows `error.response` instead of the original rejection, and
      // this action's callers (`DefaultLayout` fires it without awaiting) already rely
      // on the current failure shape. `finally` still guarantees the loader is cleared
      // on a failed fetch, which is the part that matters.
      loaderStore.start(loaderName);

      try {
        const response = await getOrderTotals(params);

        if (response.status !== 200) return;

        switch (type) {
          case 'orders': {
            ordersByOrgCount.value = response.data.data.pendingOrderCount;
            orderTotalsByOrg.value = response.data.data;
            break;
          }
          case 'claims': {
            claimsByOrgCount.value = response.data.data.pendingOrderCount;
            claimTotalsByOrg.value = response.data.data;
            break;
          }
          case 'site-orders': {
            ordersBySiteCount.value = response.data.data.pendingOrderCount;
            break;
          }
          case 'site-claims': {
            claimsBySiteCount.value = response.data.data.pendingOrderCount;
            break;
          }
          case 'vendor-orders': {
            ordersByVendorCount.value = response.data.data.pendingOrderCount;
            orderTotalsByVendor.value = response.data.data;
            break;
          }
          case 'vendor-claims': {
            claimsByVendorCount.value = response.data.data.pendingOrderCount;
            claimTotalsByVendor.value = response.data.data;
            break;
          }
        }
      } finally {
        loaderStore.end(loaderName);
      }
    },

    async processOrderPlacement(id: string, ordered: boolean, orderVersion: number) {
      const response = await toggleOrderPlaced(id, ordered, orderVersion);

      if (response.status === 200) {
        // Sync the authoritative `ordered` and (incremented) `version` from the
        // response so a subsequent toggle sends a current version — otherwise the
        // stale version triggers a 409 until the list is refetched.
        const updated = response.data.data;
        const patch = { ordered: updated.ordered, version: updated.version };

        this.orderList = this.orderList.map((order) =>
          order.id === id ? { ...order, ...patch } : order,
        );

        this.claimList = this.claimList.map((claim) =>
          claim.id === id ? { ...claim, ...patch } : claim,
        );
      }
    },

    async exportOrder(id: string, vendorName: string, orderNumber: string) {
      const toastStore = useToastStore();

      try {
        const response = await exportOrder(id);
        const contentType =
          (response.headers['content-type'] as string) ?? 'application/octet-stream';

        this.exportedOrder = new Blob([response.data], {
          type: contentType,
        });

        exportFile(this.exportedOrder, contentType, `${vendorName} ${orderNumber}`);

        toastStore.showToast(
          'success',
          t('orders.toasts.exportSuccess.title'),
          t('orders.toasts.exportSuccess.content'),
        );
      } catch (error) {
        console.error('Error downloading the file:', error);
      }
    },

    async shareOrder(id: string, data: ShareOrder) {
      const toastStore = useToastStore();

      const response = await shareOrder(id, data);

      if (response.status === 204) {
        toastStore.showToast(
          'success',
          t('orders.toasts.shareSuccess.title'),
          t('orders.toasts.shareSuccess.content'),
        );
      }
    },

    async getOrderDocs(orderId: string) {
      const response = await getOrderDocs(orderId);

      this.orderDocs = response.data.data;
    },

    addToNewOrderCart(item: BOQItem) {
      const toastStore = useToastStore();
      const existingItem = this.orderCartItems.find((cartItem: BOQItem) => cartItem.id === item.id);

      if (existingItem) {
        toastStore.showToast(
          'error',
          t('orders.createOrder.orderInventorySection.toasts.itemInCart.title'),
          t('orders.createOrder.orderInventorySection.toasts.itemInCart.content'),
        );
      } else {
        const vendorStore = useVendorStore();
        const itemVendor = vendorStore.paginatedVendors.find(
          (vendor: Vendor) => vendor.id === item.vendorId,
        );

        item.defaultRate = item.rate;
        item.vatRegistered = itemVendor?.paymentDetails.vatRegistered;
        item.total = item.rate * item.defaultQuantity!;
        item.totalInclVat = item.total * (item.vatRegistered ? VAT_MULTIPLIER : 1);
        this.orderCartItems = [...this.orderCartItems, item];
      }
    },

    removeFromNewOrderCart(itemToRemove: BOQItem) {
      this.orderCartItems = this.orderCartItems.filter(
        (item: BOQItem) => item.id !== itemToRemove.id,
      );
      this.cartItemsGroupedByVendor = [];
      this._mapCartItemsGroupedByVendor();
    },

    updateItemInNewOrderCart(item: BOQItem) {
      const itemToUpdate = this.orderCartItems.find((cartItem: BOQItem) => cartItem.id === item.id);

      if (itemToUpdate) {
        if (item.newItem) {
          if (item.vendorId) {
            const vendorStore = useVendorStore();
            const itemVendor = vendorStore.paginatedVendors.find(
              (vendor: Vendor) => vendor.id === item.vendorId,
            );

            item.vatRegistered = itemVendor?.paymentDetails.vatRegistered;
          }
        } else {
          itemToUpdate.defaultQuantity = Math.min(item.defaultQuantity!, item.remaining);
        }

        itemToUpdate.total = item.rate * item.defaultQuantity!;
        item.itemVariation =
          (item.desiredQuantity ? item.desiredQuantity : item.defaultQuantity!) *
            item.variationRate! -
            item.total! || 0;
        itemToUpdate.totalInclVat =
          (item.total! + item.itemVariation) * (item.vatRegistered ? VAT_MULTIPLIER : 1);
      }

      if (itemToUpdate?.specification || itemToUpdate?.vendorId) {
        this.cartItemsGroupedByVendor = [];
        this._mapCartItemsGroupedByVendor();
      }
    },

    _mapCartItemsGroupedByVendor() {
      this.cartItemsGroupedByVendor = [];

      for (const item of this.orderCartItems) {
        const existingVendor = this.cartItemsGroupedByVendor.find(
          (vendor) => vendor.vendorId === item.vendorId,
        );

        const amountToPayVendor = item.totalInclVat ?? 0;
        const amountExclVat = item.total ?? 0;

        if (existingVendor) {
          existingVendor.amountToPayVendor += amountToPayVendor;
          existingVendor.amountExclVat += amountExclVat;
        } else {
          this.cartItemsGroupedByVendor.push({
            vendorId: item.vendorId,
            vendorName: item.vendorName,
            amountToPayVendor,
            amountExclVat,
          });
        }
      }
    },

    async addRetention(
      id: string,
      data: {
        percent: number;
        dueDate?: string;
        assigneeId?: string;
      }[],
      version: number,
    ) {
      const response = await addRetention(id, data, version);
      this.orderClaim = response.data.data;

      analyticsService.capture('claim_retention_added', {
        org_id: selectedOrgId.value!,
        order_id: id,
      });
    },

    async editRetention(
      orderId: string,
      retentionId: string,
      data: {
        percent: number;
        /** `null` clears it — see the note on the service's own signature. */
        dueDate?: string | null;
        assigneeId?: string | null;
      },
      version: number,
      type?: string,
    ) {
      const toastStore = useToastStore();

      const response = await editRetention(orderId, retentionId, data, version);

      if (response.status === 200) {
        toastStore.showToast(
          'success',
          t('orders.createOrder.orderRequests.deductions.retentions.detailsUpdateSuccess.title', {
            type: type,
          }),
          t('orders.createOrder.orderRequests.deductions.retentions.detailsUpdateSuccess.content'),
        );
        this.orderClaim = response.data.data;
      }
    },

    async deleteRetention(orderId: string, retentionId: string) {
      const response = await deleteRetention(orderId, retentionId);
      this.orderClaim = response.data.data;
    },

    async addValueContraCharge(
      id: string,
      data: {
        amount: number;
      }[],
      version: number,
    ) {
      const response = await addValueContraCharge(id, data, version);
      this.orderClaim = response.data.data;

      analyticsService.capture('claim_value_contra_added', {
        org_id: selectedOrgId.value!,
        order_id: id,
      });
    },

    async editValueContraCharge(
      orderId: string,
      contraId: string,
      data: {
        amount: number;
      },
      version: number,
      type?: string,
    ) {
      const toastStore = useToastStore();

      const response = await editValueContraCharge(orderId, contraId, data, version);

      if (response.status === 200) {
        toastStore.showToast(
          'success',
          t(
            'orders.createOrder.orderRequests.deductions.valueContraCharges.detailsUpdateSuccess.title',
            {
              type:
                type ??
                t(
                  'orders.createOrder.orderRequests.deductions.valueContraCharges.detailsUpdateSuccess.fallbackType',
                ),
            },
          ),
          t(
            'orders.createOrder.orderRequests.deductions.valueContraCharges.detailsUpdateSuccess.content',
          ),
        );
        this.orderClaim = response.data.data;
      }
    },

    async deleteValueContraCharge(orderId: string, contraId: string) {
      const response = await deleteValueContraCharge(orderId, contraId);
      this.orderClaim = response.data.data;
    },

    async addItemContraCharge(
      id: string,
      data: ItemContraChargeRequest[],
      version: number,
      siteId?: string,
    ) {
      const boqStore = useBoqStore();
      const response = await addItemContraCharge(id, data, version);
      this.orderClaim = response.data.data;

      if (siteId && boqStore.hasBoqForSite(siteId)) {
        await boqStore.getBOQ(siteId, true);
      }

      analyticsService.capture('claim_item_contra_added', {
        org_id: selectedOrgId.value!,
        order_id: id,
        siteId: siteId ?? '',
      });
    },

    async editItemContraCharge(
      orderId: string,
      contraId: string,
      data: ItemContraChargeRequest,
      version: number,
      siteId?: string,
      type?: string,
    ) {
      const boqStore = useBoqStore();
      const toastStore = useToastStore();

      const response = await editItemContraCharge(orderId, contraId, data, version);

      if (response.status !== 200) return;

      toastStore.showToast(
        'success',
        t(
          'orders.createOrder.orderRequests.deductions.itemContraCharges.detailsUpdateSuccess.title',
          {
            type:
              type ??
              t(
                'orders.createOrder.orderRequests.deductions.itemContraCharges.detailsUpdateSuccess.fallbackType',
              ),
          },
        ),
        t(
          'orders.createOrder.orderRequests.deductions.itemContraCharges.detailsUpdateSuccess.content',
        ),
      );
      this.orderClaim = response.data.data;

      // A contra charge writes a log onto the BOQ line it names, so the BOQ is stale either way.
      if (siteId && boqStore.hasBoqForSite(siteId)) {
        await boqStore.getBOQ(siteId, true);
      }
    },

    async deleteItemContraCharge(orderId: string, contraId: string, siteId?: string) {
      const boqStore = useBoqStore();
      const response = await deleteItemContraCharge(orderId, contraId);
      this.orderClaim = response.data.data;

      if (siteId && boqStore.hasBoqForSite(siteId)) {
        await boqStore.getBOQ(siteId, true);
      }
    },

    clearNewOrderCart() {
      this.orderCartItems = [];
      this.newOrderActiveTab = '#order';
      this.cartItemsGroupedByVendor = [];
      this._mapCartItemsGroupedByVendor();
      this.additions = [];
      for (const vendor of this.cartItemsGroupedByVendor) {
        vendor.additions = [];
      }
    },

    async getOrderComplianceInfo(orderId: string) {
      try {
        const response = await getOrderComplianceInfo(orderId);

        if (response.status === 200) {
          this.complianceInfo = response.data.data;
          this.complianceInfoOrderId = orderId;
        } else {
          console.error(`Failed to fetch order compliance info. Status: ${response.status}`);
          // Reset to empty so stale rules from a previous order don't bleed through,
          // and advance the ID so the additionalOrderDocuments guard doesn't permanently hide docs.
          this.complianceInfo = {
            rules: [],
            overallCompliance: { isCompliant: true, failures: [], warnings: [] },
          };
          this.complianceInfoOrderId = orderId;
        }
      } catch (error) {
        console.error(`An error occurred while fetching order compliance info`, error);
        // Same degraded-state reset on network/unexpected errors.
        this.complianceInfo = {
          rules: [],
          overallCompliance: { isCompliant: true, failures: [], warnings: [] },
        };
        this.complianceInfoOrderId = orderId;
      }
    },

    requestExitCompleteClaim(): void {
      if (this.hasPendingClaimDocuments) {
        this.exitClaimRequested = true;
      } else {
        this.completeClaim = false;
      }
    },
  },
});
